// Montagereihenfolge — praxisnahe, deterministische Heuristik.
// Identisch implementiert in AssemblyPlan.order (app/models/assembly_plan.rb):
// gleiche Regeln, gleiche Regel-Reihenfolge, gleiche Schlüssel. Änderungen
// immer auf beiden Seiten (und in beiden Testdateien) nachziehen.
//
// Regeln in dieser Reihenfolge:
//   1. Baugruppen zusammenhalten — Teile mit gemeinsamer meta.door_id /
//      meta.drawer_id / meta.cabinet_id bilden einen Block, der am Stück
//      montiert wird. Spezifischere Gruppe gewinnt (door > drawer > cabinet),
//      damit eine Tür im Korpus nicht im Korpusblock verschwindet.
//      Teile ohne Gruppe sind ihr eigener Ein-Teil-Block.
//   2. Phase je Teil — Korpus (0) → Rückwand (1) → Front (2). Maßgeblich ist
//      `meta.part_type` (editor/part_types.js). Fehlt das Flag (Altbestand),
//      greift die frühere Heuristik: Front = Tür/Schublade (meta.door_id/
//      meta.drawer_id, Topfband-Beschlag, Topfband-Bohrung ab Ø30 oder Name
//      Tür/Front/Schublade), Rückwand = Name Rück(en)wand, sonst Korpus.
//   3. Innerhalb eines Blocks: Phase, dann pos_y (unten→oben), dann pos_x
//      (links→rechts), dann client_id. So kommt die Rückwand eines Korpus
//      nach dem Rahmen und vor eingehängten Fronten.
//   4. Phase eines Blocks: Korpusgruppe = Korpus, Tür-/Schubladengruppe =
//      Front (Fronten zuletzt), Einzelteil = seine eigene Teil-Phase.
//   5. Einheiten — ein Korpus und die ihm zugeordneten Fronten bilden eine
//      Einheit und werden am Stück montiert (Korpus A, Tür A, Korpus B,
//      Tür B statt beide Korpusse und dann beide Türen). Zugehörigkeit über
//      meta.belongs_to_cabinet am Anker der Front, sonst über deren
//      meta.cabinet_id, sonst räumlich (cabinetOfFront). Innerhalb einer
//      Einheit steht der Korpusblock vorn, danach nach (Block-Phase, Anker).
//   6. Einheiten nach (Phase, Anker) ihres führenden Blocks, Anker = erstes
//      Teil des Blocks nach Regel 3. Letzter Tie-Break bleibt client_id — die
//      Reihenfolge ist damit unabhängig von der Eingabereihenfolge.
import { PART_TYPES, DEFAULT_PART_TYPE } from "./part_types"
import { partAabb } from "./snapping"

const CARCASS = 0
const BACK = 1
const FRONT = 2

const PHASE_BY_TYPE = { korpus: CARCASS, rueckwand: BACK, front: FRONT }

// Fallback-Heuristiken für Altbestände ohne meta.part_type (Regel 2).
const BACK_NAME = /r(ü|ue)ck(en)?wand/i
const FRONT_NAME = /^\s*(t(ü|ue)r|door|front|schublade)/i
// Topfband-Topf ist Ø35; ab Ø30 gilt eine Flächenbohrung als Bandtopf.
const HINGE_CUP_MIN_DIAMETER_MM = 30

// Wie weit darf der Mittelpunkt einer Front außerhalb der Korpushülle liegen,
// damit sie noch zu diesem Korpus gehört? Aufgesetzte Fronten sitzen knapp
// davor (halbe Frontstärke + Fuge), Griffleisten ragen etwas über.
const FRONT_FIT_MARGIN_MM = 100

const GEOMETRY_KEYS = [ "length_mm", "width_mm", "thickness_mm", "pos_x", "pos_y", "pos_z" ]

const metaOf = (part) => part.meta ?? {}

// Baugruppen-Schlüssel eines Teils (Regel 1) — null = eigener Block.
function groupId(part) {
  const meta = metaOf(part)
  return meta.door_id ?? meta.drawer_id ?? meta.cabinet_id ?? null
}

function groupKind(part) {
  const meta = metaOf(part)
  if (meta.door_id) return "door"
  if (meta.drawer_id) return "drawer"
  if (meta.cabinet_id) return "cabinet"
  return null
}

function isFront(part) {
  const meta = metaOf(part)
  if (meta.door_id || meta.drawer_id) return true
  if ((meta.hardware ?? []).some((entry) => entry?.type === "topfband")) return true
  const bores = meta.cutouts ?? []
  if (bores.some((cut) => cut?.shape === "circle" && (cut.axis ?? "y") === "y" &&
    (cut.diameter_mm ?? 0) >= HINGE_CUP_MIN_DIAMETER_MM)) return true
  return FRONT_NAME.test(part.name ?? "")
}

// Bauteil-Typ, mit dem die Montage rechnet: gesetztes Flag schlägt alles,
// sonst die alte Namens-/Bohrungs-Heuristik (Altbestand, Regel 2).
// Wird auch im Details-Panel angezeigt, damit dort steht, wovon die
// Montageanleitung ausgeht.
export function effectivePartType(part) {
  const flag = metaOf(part).part_type
  if (PART_TYPES.includes(flag)) return flag
  if (isFront(part)) return "front"
  if (BACK_NAME.test(part.name ?? "")) return "rueckwand"
  return DEFAULT_PART_TYPE
}

// Phase eines einzelnen Teils (Regel 2)
function partPhase(part) {
  return PHASE_BY_TYPE[effectivePartType(part)]
}

// Phase eines Blocks (Regel 4)
function blockPhase(members) {
  const kind = groupKind(members[0])
  if (kind === "door" || kind === "drawer") return FRONT
  if (kind === "cabinet") return CARCASS
  return partPhase(members[0])
}

const compareId = (a, b) => (a < b ? -1 : a > b ? 1 : 0)

// Lage-Tie-Break (Regel 3/5): unten→oben, links→rechts, client_id
const comparePosition = (a, b) =>
  (a.pos_y - b.pos_y) || (a.pos_x - b.pos_x) || compareId(a.client_id, b.client_id)

const compareInBlock = (a, b) => (partPhase(a) - partPhase(b)) || comparePosition(a, b)

// Innerhalb einer Einheit (Regel 5): Korpusblock zuerst, dann nach Phase/Lage.
const compareInUnit = (a, b) =>
  ((a.cabinetId ? 0 : 1) - (b.cabinetId ? 0 : 1)) ||
  (a.phase - b.phase) || comparePosition(a.anchor, b.anchor)

const hasGeometry = (part) => GEOMETRY_KEYS.every((key) => Number.isFinite(part?.[key]))

const aabbOf = (part) => partAabb({
  ...part, rot_x: part.rot_x ?? 0, rot_y: part.rot_y ?? 0, rot_z: part.rot_z ?? 0
})

const unionAabb = (boxes) => boxes.reduce((acc, box) => ({
  min: acc.min.map((value, i) => Math.min(value, box.min[i])),
  max: acc.max.map((value, i) => Math.max(value, box.max[i]))
}))

// Abstand eines Punktes zur Hülle in der Maximumsnorm; 0 = innerhalb.
const outsideDistance = (point, box) => Math.max(
  ...[ 0, 1, 2 ].map((i) => Math.max(box.min[i] - point[i], point[i] - box.max[i], 0))
)

// Räumliche Zuordnung einer Front zu einem Korpus — reine Hilfsfunktion und
// Rückfall der Sortierung (Regel 5), wenn kein meta.belongs_to_cabinet gesetzt
// ist (Bestandsprojekte). `parts` sind alle Teile des Projekts.
// Der Mittelpunkt der Front muss in der um FRONT_FIT_MARGIN_MM aufgeblähten
// Hülle der Korpusteile liegen: innerhalb (eingelassen) oder knapp davor
// (aufgesetzt). Bei mehreren Treffern gewinnt der nächstgelegene Korpus,
// Gleichstand nach Gruppen-Id — damit ist das Ergebnis deterministisch.
// Liefert die cabinet_id oder null.
export function cabinetOfFront(front, parts = []) {
  if (!hasGeometry(front)) return null
  const center = [ front.pos_x, front.pos_y, front.pos_z ]
  const cabinets = new Map()
  for (const part of parts) {
    // Nur echte Korpusteile — eine Tür mit cabinet_id gehört nicht zur Hülle.
    if (groupKind(part) !== "cabinet" || !hasGeometry(part)) continue
    const id = metaOf(part).cabinet_id
    if (!cabinets.has(id)) cabinets.set(id, [])
    cabinets.get(id).push(aabbOf(part))
  }

  let best = null
  for (const [ id, boxes ] of cabinets) {
    const distance = outsideDistance(center, unionAabb(boxes))
    if (distance > FRONT_FIT_MARGIN_MM) continue
    if (!best || distance < best.distance || (distance === best.distance && compareId(id, best.id) < 0)) {
      best = { id, distance }
    }
  }
  return best?.id ?? null
}

// Korpus, zu dem ein Block gehört (Regel 5) — null, wenn keiner passt.
function hostCabinet(block, parts, cabinetIds) {
  const meta = metaOf(block.anchor)
  if (cabinetIds.has(meta.belongs_to_cabinet)) return meta.belongs_to_cabinet
  if (cabinetIds.has(meta.cabinet_id)) return meta.cabinet_id
  // Räumlicher Rückfall nur für Fronten: ein loses Teil im Korpusvolumen
  // (z. B. eine Sockelblende) soll seinen Platz in der Reihenfolge behalten.
  if (block.phase !== FRONT) return null
  const id = cabinetOfFront(block.anchor, parts)
  return cabinetIds.has(id) ? id : null
}

export function assemblyOrder(parts) {
  const grouped = new Map()
  for (const part of parts) {
    const id = groupId(part)
    const key = id ? `g:${id}` : `p:${part.client_id}`
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key).push(part)
  }

  const blocks = [ ...grouped.values() ].map((members) => {
    const sorted = [ ...members ].sort(compareInBlock)
    const anchor = sorted[0]
    return {
      members: sorted, anchor, phase: blockPhase(sorted),
      cabinetId: groupKind(anchor) === "cabinet" ? groupId(anchor) : null
    }
  })

  const cabinetIds = new Set(blocks.map((block) => block.cabinetId).filter(Boolean))
  const units = new Map()
  blocks.forEach((block, index) => {
    const host = block.cabinetId ?? hostCabinet(block, parts, cabinetIds)
    const key = host ? `c:${host}` : `b:${index}`
    if (!units.has(key)) units.set(key, [])
    units.get(key).push(block)
  })

  return [ ...units.values() ]
    .map((members) => [ ...members ].sort(compareInUnit))
    .sort((a, b) => (a[0].phase - b[0].phase) || comparePosition(a[0].anchor, b[0].anchor))
    .flatMap((unit) => unit.flatMap((block) => block.members))
}
